import React, { useEffect, useMemo, useState } from 'react';
import { Alert } from '@televet/kibble-ui/build/components/Alert';
import {
  LoyaltyProgramStatus,
  LoyaltyRewardRedemptionStatus,
  useGetFirstLoyaltyRewardRedemptionQuery,
  useGetPetParentLoyaltyProgramDataQuery,
  useSubscribeToPendingFulfillmentRewardUpdateSubscription,
} from 'shared/types/graphql';
import { useSidePanelSearch } from 'shared/components/SidePanel/components/SearchPanel/state/providers/SearchPanelProvider';
import { RewardsTab } from 'shared/components/SidePanel/components/SearchPanel/components/LoyaltyProgram/components/RewardsPanel';

interface ILoyaltyProgramBannerProps {
  clinicPetParentId: string;
}

const LoyaltyProgramBanner = ({ clinicPetParentId }: ILoyaltyProgramBannerProps): JSX.Element => {
  const [showLoyaltyProgramAlert, setShowLoyaltyProgramAlert] = useState(false);
  const { viewLoyaltyRewards, viewClinicPetParent } = useSidePanelSearch();
  const { data: loyaltyProgramData, loading: isLoyaltyProgramDataLoading } = useGetPetParentLoyaltyProgramDataQuery({
    variables: {
      where: {
        id: {
          equals: clinicPetParentId || '',
        },
      },
    },
    skip: !clinicPetParentId,
  });

  const accountId = loyaltyProgramData?.findFirstClinicPetParent?.loyaltyAccountHolder?.account?.id;

  const {
    data: pendingFulfillmentRewardData,
    refetch: refetchPendingFulFillmentRewardData,
    loading: isPendingFulfillmentRewardDataLoading,
  } = useGetFirstLoyaltyRewardRedemptionQuery({
    variables: {
      where: {
        account: {
          id: {
            equals: accountId || '',
          },
          program: {
            status: {
              notIn: [LoyaltyProgramStatus.Archived, LoyaltyProgramStatus.Draft],
            },
          },
        },
        status: {
          equals: LoyaltyRewardRedemptionStatus.PendingFulfillment,
        },
      },
    },
    skip: !accountId,
    onCompleted: () => {
      if (pendingFulfillmentRewardData?.findFirstLoyaltyRewardRedemption) {
        setShowLoyaltyProgramAlert(true);
      }
    },
  });

  useSubscribeToPendingFulfillmentRewardUpdateSubscription({
    variables: {
      where: {
        id: pendingFulfillmentRewardData?.findFirstLoyaltyRewardRedemption?.id || '',
      },
    },
    skip: !accountId,
    onData: ({ data }) => {
      if (data.data?.loyaltyRewardRedemptionUpdated) {
        refetchPendingFulFillmentRewardData();
      }
    },
  });
  const pendingFulfillmentReward = useMemo(
    () => pendingFulfillmentRewardData?.findFirstLoyaltyRewardRedemption,
    [pendingFulfillmentRewardData],
  );

  useEffect(() => {
    if (isLoyaltyProgramDataLoading || isPendingFulfillmentRewardDataLoading) {
      return;
    }
    setShowLoyaltyProgramAlert(!!pendingFulfillmentReward);
  }, [isLoyaltyProgramDataLoading, isPendingFulfillmentRewardDataLoading, pendingFulfillmentReward]);

  const handlecloseAlert = (): void => {
    setShowLoyaltyProgramAlert(false);
  };
  const openPetParentSidePanelProfile = (): void => {
    viewClinicPetParent({ clinicPetParentId });
    viewLoyaltyRewards(RewardsTab.CurrentRewards);
    refetchPendingFulFillmentRewardData();
  };

  useEffect(() => {
    refetchPendingFulFillmentRewardData();
    // refetch on mount only since InvoiceNotification closes when user either clicks on 'View Current Rewards' or navigates away
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {showLoyaltyProgramAlert && !isPendingFulfillmentRewardDataLoading && !isLoyaltyProgramDataLoading && (
        <Alert
          iconProps={{ name: 'award' }}
          my="10px"
          title="Apply Rewards?"
          description="This client may have rewards that can be used in this invoice. Would you like to check?"
          buttonProps={{
            buttonText: 'View Rewards',
            buttonPosition: 'bottomLeft',
            onClick: openPetParentSidePanelProfile,
          }}
          status="warning"
          onClose={handlecloseAlert}
        />
      )}
    </>
  );
};

export default LoyaltyProgramBanner;
